import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'

import eventApi from '_api/event'
import { convertToCurrentGMT } from '_utils/function'
import useShareLinkEvent from '_hooks/useShareLinkEvent'

import { downloadEventTemplateExcel, printEventDetailPage } from '../HostedEvent/helpers/eventTemplateExport'

/**
 * Shared card actions for the hosted-events list (tile & list views).
 * Extracted from the duplicated logic in MyEventItem.jsx / MyEventItemGrid.jsx.
 */
const useEventCardActions = () => {
  const history = useHistory()
  const { addToast } = useToasts()
  const { handleShare, isLoading: isSharing } = useShareLinkEvent()
  const [isExporting, setIsExporting] = useState(false)

  const fetchEventDetail = async (eventId) => {
    const { msgResp: eventDetail } = await eventApi.getEventDetail({
      eid: eventId,
      isGetProducts: true,
      isGetDeliveryZones: true,
    })
    if (!eventDetail?.id) return null
    return {
      ...eventDetail,
      closingTime: convertToCurrentGMT(eventDetail.closingTime),
      pickupTime: convertToCurrentGMT(eventDetail.pickupTime),
    }
  }

  // EventHosting fetches the event itself (useFetchEventDetail) from these
  // query params, so there's no need to pre-fetch/stash it here like the
  // old Create page required (setCreateEventData + separate /update route).
  const useAsTemplate = (eventId) => {
    if (!eventId) return
    history.push(`/event/hosting?id=${eventId}&fromReHost=1`)
  }

  const editEvent = (eventId) => {
    if (!eventId) return
    history.push(`/event/hosting?id=${eventId}`)
  }

  const exportTemplate = async (eventId, format) => {
    if (!eventId) return

    if (format === 'pdf') {
      printEventDetailPage(eventId)
      return
    }

    setIsExporting(true)
    try {
      const eventDetail = await fetchEventDetail(eventId)
      if (!eventDetail?.products?.length) {
        addToast('This event has no items to export', { appearance: 'info', autoDismiss: true })
        return
      }
      downloadEventTemplateExcel(eventDetail)
    } catch (error) {
      addToast(error?.msgResp || 'Failed to export event template', {
        appearance: 'error',
        autoDismiss: true,
      })
    } finally {
      setIsExporting(false)
    }
  }

  const goToManageOrders = (eventId) => {
    if (!eventId) return
    history.push(`/event/manage-orders?eventId=${eventId}`)
  }

  const shareEvent = (eventId, onSuccess) => {
    handleShare(eventId, onSuccess)
  }

  return {
    useAsTemplate,
    editEvent,
    goToManageOrders,
    shareEvent,
    isSharing,
    exportTemplate,
    isExporting,
  }
}

export default useEventCardActions
